import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { getApiErrorMessage } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { confirmToast } from "../../lib/confirmToast";
import CampaignDetailsDialog from "../../components/CampaignDetailsDialog";
import { Trophy } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { Ban, Plus, Pencil, Megaphone, Search, Loader2, Calendar, Zap, RotateCcw, Store } from "lucide-react";

const AdminCampanhas = () => {
  const { isMaster } = useAuth();
  const [campanhas, setCampanhas] = useState([]);
  const [lojas, setLojas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rankedCampaign, setRankedCampaign] = useState(null);
  const [formData, setFormData] = useState({
    loja_id: "",
    loja_ids: [],
    nome: "",
    descricao: "",
    data_inicio: "",
    data_fim: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [campanhasRes, lojasRes] = await Promise.all([
        api.get("/campanhas"),
        api.get("/lojas"),
      ]);
      setCampanhas(campanhasRes.data);
      setLojas(lojasRes.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao carregar dados."));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const lojaIds = Array.from(new Set([
        formData.loja_id,
        ...formData.loja_ids,
        ...(!isMaster && lojas.length === 1 ? [lojas[0].id] : []),
      ].filter(Boolean)));
      if (lojaIds.length === 0) {
        toast.error("Selecione ao menos uma loja para esta campanha.");
        return;
      }

      const payload = {
        ...formData,
        loja_id: lojaIds[0],
        loja_ids: lojaIds,
        regras_json: campanhas.find((campanha) => campanha.id === editing)?.regras_json || {},
      };
      
      if (editing) {
        await api.put(`/campanhas/${editing}`, payload);
        toast.success("Campanha atualizada com sucesso!");
      } else {
        await api.post("/campanhas", payload);
        toast.success("Campanha criada com sucesso!");
      }
      setDialogOpen(false);
      setEditing(null);
      setFormData({ loja_id: "", loja_ids: [], nome: "", descricao: "", data_inicio: "", data_fim: "" });
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao salvar campanha."));
    }
  };

  const handleEdit = (campanha) => {
    const campaignLojaIds = Array.from(new Set([campanha.loja_id, ...(campanha.loja_ids || [])].filter(Boolean)));
    const lojaIds = campaignLojaIds.length > 0
      ? campaignLojaIds
      : isMaster
        ? lojas.map((loja) => loja.id)
        : [];
    setEditing(campanha.id);
    setFormData({
      loja_id: lojaIds[0] || "",
      loja_ids: lojaIds,
      nome: campanha.nome,
      descricao: campanha.descricao,
      data_inicio: campanha.data_inicio.split("T")[0],
      data_fim: campanha.data_fim.split("T")[0],
    });
    setDialogOpen(true);
  };

  const toggleStore = (lojaId) => {
    setFormData((current) => {
      const lojaIds = current.loja_ids.includes(lojaId)
        ? current.loja_ids.filter((id) => id !== lojaId)
        : [...current.loja_ids, lojaId];
      return { ...current, loja_ids: lojaIds, loja_id: lojaIds[0] || "" };
    });
  };

  const toggleAllStores = () => {
    setFormData((current) => {
      const allSelected = lojas.length > 0 && lojas.every((loja) => current.loja_ids.includes(loja.id));
      const lojaIds = allSelected ? [] : lojas.map((loja) => loja.id);
      return { ...current, loja_ids: lojaIds, loja_id: lojaIds[0] || "" };
    });
  };

  const getCampaignStoresLabel = (campanha) => {
    const lojaIds = Array.from(new Set([campanha.loja_id, ...(campanha.loja_ids || [])].filter(Boolean)));
    if (lojaIds.length === 0) return "Todas as lojas (cadastro anterior)";
    const names = lojaIds.map((id) => lojas.find((loja) => loja.id === id)?.nome).filter(Boolean);
    return names.length > 0 ? names.join(", ") : `${lojaIds.length} ${lojaIds.length === 1 ? "loja" : "lojas"}`;
  };

  const deleteCampanha = async (id) => {
    try {
      await api.delete(`/campanhas/${id}`);
      toast.success("Campanha desativada com sucesso!");
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao desativar campanha."));
    }
  };

  const handleDelete = (id) => {
    confirmToast({
      title: "Desativar campanha?",
      description: "A campanha deixará de pontuar novas vendas.",
      confirmLabel: "Desativar",
      variant: "danger",
      onConfirm: () => deleteCampanha(id),
    });
  };

  const reactivateCampanha = async (id) => {
    try {
      await api.put(`/campanhas/${id}/reativar`);
      toast.success("Campanha reativada com sucesso!");
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao reativar campanha."));
    }
  };

  const handleReactivate = (id) => {
    confirmToast({
      title: "Reativar campanha?",
      description: "Ela voltará a pontuar novas vendas dentro do período configurado.",
      confirmLabel: "Reativar",
      onConfirm: () => reactivateCampanha(id),
    });
  };

  const openEditor = (campanha) => {
    navigate(`/admin/campanhas/editor/${campanha.id}`);
  };

  const filteredCampanhas = campanhas.filter(
    (c) =>
      c.nome.toLowerCase().includes(search.toLowerCase()) ||
      c.descricao.toLowerCase().includes(search.toLowerCase())
  );

  const isActive = (campanha) => {
    const now = new Date();
    const start = new Date(campanha.data_inicio);
    const end = new Date(campanha.data_fim);
    return campanha.status === "ativa" && now >= start && now <= end;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-campanhas">
      <CampaignDetailsDialog campaign={rankedCampaign} mode="ranking" onClose={() => setRankedCampaign(null)} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Campanhas</h1>
          <p className="text-slate-600 mt-1">Gerencie as campanhas de incentivo</p>
        </div>
        <div className="flex gap-3">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="btn-primary"
                onClick={() => {
                  setEditing(null);
                  setFormData({
                    loja_id: !isMaster && lojas.length === 1 ? lojas[0].id : "",
                    loja_ids: !isMaster && lojas.length === 1 ? [lojas[0].id] : [],
                    nome: "",
                    descricao: "",
                    data_inicio: "",
                    data_fim: "",
                  });
                }}
                data-testid="add-campanha-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova Campanha
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editing ? "Editar Campanha" : "Nova Campanha"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome da campanha</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="input-field"
                    required
                    data-testid="campanha-nome-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="descricao">Descrição</Label>
                  <Input
                    id="descricao"
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    className="input-field"
                    required
                    data-testid="campanha-descricao-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Lojas participantes</Label>
                  <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
                    {lojas.length > 1 && (
                      <Label
                        htmlFor="campanha-todas-lojas"
                        className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 font-medium text-slate-800 hover:bg-slate-50"
                      >
                        <Checkbox
                          id="campanha-todas-lojas"
                          checked={lojas.length > 0 && lojas.every((loja) => formData.loja_ids.includes(loja.id))}
                          onCheckedChange={toggleAllStores}
                        />
                        Selecionar todas as lojas
                      </Label>
                    )}
                    {lojas.map((loja) => (
                      <Label
                        key={loja.id}
                        htmlFor={`campanha-loja-${loja.id}`}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-slate-50"
                      >
                        <Checkbox
                          id={`campanha-loja-${loja.id}`}
                          checked={formData.loja_ids.includes(loja.id)}
                          onCheckedChange={() => toggleStore(loja.id)}
                        />
                        <span className="min-w-0 flex-1 text-sm text-slate-700">{loja.nome}</span>
                      </Label>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">A campanha será aplicada às vendas dos parceiros vinculados a estas lojas.</p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="data_inicio">Data início</Label>
                    <Input
                      id="data_inicio"
                      type="date"
                      value={formData.data_inicio}
                      onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                      className="input-field"
                      required
                      data-testid="campanha-inicio-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="data_fim">Data fim</Label>
                    <Input
                      id="data_fim"
                      type="date"
                      value={formData.data_fim}
                      onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                      className="input-field"
                      required
                      data-testid="campanha-fim-input"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" className="btn-primary flex-1" data-testid="save-campanha-btn">
                    {editing ? "Atualizar" : "Criar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          placeholder="Buscar campanhas..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-10"
          data-testid="search-campanhas-input"
        />
      </div>

      {/* Cards Grid */}
      {loading ? (
        <div className="p-12 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCampanhas.map((campanha) => (
            <div
              key={campanha.id}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all group"
            >
              <div className={`h-2 ${isActive(campanha) ? "bg-success" : "bg-slate-300"}`} />
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Megaphone className="h-6 w-6 text-primary" />
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isActive(campanha)
                      ? "bg-green-100 text-green-700"
                      : campanha.status === "ativa"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-slate-100 text-slate-600"
                  }`}>
                    {isActive(campanha) ? "Ativa" : campanha.status === "ativa" ? "Agendada" : "Inativa"}
                  </span>
                </div>
                
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                  {campanha.nome}
                </h3>
                <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                  {campanha.descricao}
                </p>
                
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {new Date(campanha.data_inicio).toLocaleDateString('pt-BR')} - {new Date(campanha.data_fim).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                <div className="mb-4 flex items-start gap-2 text-sm text-slate-500">
                  <Store className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="line-clamp-2">{getCampaignStoresLabel(campanha)}</span>
                </div>

                <div className="flex gap-2 pt-4 border-t border-slate-100">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditor(campanha)}
                    className="flex-1"
                    data-testid={`editor-campanha-${campanha.id}`}
                  >
                    <Zap className="h-4 w-4 mr-1" />
                    Regras
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setRankedCampaign(campanha)}><Trophy className="mr-2 h-4 w-4" />Ranking</Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(campanha)}
                    className="text-slate-500 hover:text-primary"
                    data-testid={`edit-campanha-${campanha.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {campanha.status === "inativa" ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleReactivate(campanha.id)}
                      className="text-slate-500 hover:text-primary"
                      title="Reativar campanha"
                      aria-label="Reativar campanha"
                      data-testid={`reactivate-campanha-${campanha.id}`}
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(campanha.id)}
                      className="text-slate-500 hover:text-red-500"
                      title="Desativar campanha"
                      aria-label="Desativar campanha"
                      data-testid={`delete-campanha-${campanha.id}`}
                    >
                      <Ban className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {filteredCampanhas.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-500">
              {search ? "Nenhuma campanha encontrada" : "Nenhuma campanha cadastrada ainda"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCampanhas;
